/**
 * One live run: start it, watch it, stop it (#2158 slice 3a).
 *
 * **Why this exists.** All of this lived inside `TestRunner`, which was the
 * only page that could start a run. Slice 3a puts start controls on Runs too,
 * and that page loads once with no socket — so without extracting first, the
 * second caller would have meant a second copy of the WebSocket wiring and the
 * run-state reducer. Two copies of a socket lifecycle is exactly the kind of
 * duplication that drifts silently: one gets a bug fix, the other does not.
 *
 * Deliberately extracted with the logic moved VERBATIM, so the refactor can be
 * reviewed as behaviour-neutral. Anything that looks odd here almost certainly
 * looked odd before; this is not the change that fixes it.
 *
 * **What it owns.** The run being watched, its phase, the backend's progress
 * percentage, and the socket. Not the templates, not the page chrome, not the
 * results rendering — those stay with whichever page renders them, because
 * they differ per page and this does not.
 *
 * **Reattach on mount.** `resume()` asks the server for an already-running run
 * and re-subscribes to it. Without it, a reload mid-run left the operator on a
 * page that looked idle while the run carried on server-side.
 *
 * **The page watches; the server records.** The server saves a run's status,
 * results and timing as the run goes. This hook only listens. When its socket
 * drops it reconnects on its own, quietly, and never saves the run as failed:
 * a dropped connection says nothing about the run. It used to, and runs that
 * were still going, or had already passed, were saved as failed with a wrong
 * start time.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  createWebSocketConnection,
  getActiveRuns,
  getTestRunStatus,
  cancelTestRun as apiCancelTestRun,
  triggerRun,
  testRunsAPI,
  type TestRunProgress,
  type TestRunPhase,
  type CurrentTest,
} from '@/lib/api'
import type { TestRun, PlanCategoryName } from '@/types'

/** The run's status as the UI models it.
 *
 * Not a narrowing — `TestRunProgress.status` is already a subset of
 * `TestRun.status`, which also has `'pending'`. What it guarantees is
 * that anything not terminal reads as running, INCLUDING a value the
 * wire type does not declare: that type is a hand-written mirror of the
 * server's, and a run that arrived as `'pending'` would otherwise stop
 * the card saying "Running…" while the run was still going.
 *
 * Written once because #2206 added a second reader — attaching to a
 * named run maps the same field — and two copies of a mapping is how
 * the two drift. */
function toStatus(progress: TestRunProgress): TestRun['status'] {
  return progress.status === 'completed' ? 'completed'
    : progress.status === 'failed' ? 'failed'
      : progress.status === 'cancelled' ? 'cancelled' : 'running'
}

/** Progress results → the local `TestRun.results` shape. Shared by the resume
 * path and the live-progress path, which built the same object twice. */
function toResults(progress: TestRunProgress): TestRun['results'] {
  return progress.results.map((r) => ({
    id: r.id,
    runId: r.runId,
    category: r.category as PlanCategoryName,
    name: r.name,
    status: r.status,
    steps: r.steps.map((s) => ({
      id: s.id,
      name: s.name,
      status: s.status as 'pending' | 'running' | 'passed' | 'failed' | 'skipped',
      duration: s.duration,
      error: s.error,
    })),
    duration: r.duration,
    error: r.error,
    screenshot: r.screenshot,
  }))
}

/** Progress logs → local logs. Same duplication as above. */
function toLogs(progress: TestRunProgress): TestRun['logs'] {
  return progress.logs.map((l) => ({
    timestamp: new Date(l.timestamp),
    level: l.level,
    message: l.message,
    details: l.details,
    agent: l.agent,
  }))
}

export interface StartRunOptions {
  /** Plain-language name for the kind of run, used in the opening log line.
   * The caller owns this because it is the caller that knows which button was
   * pressed. */
  label?: string
  /** The URL the run targets, logged so the operator can confirm at a glance
   * that it went where they expected. Optional: a caller that does not know it
   * simply omits the line. */
  targetUrl?: string
  /** Per-run candidate picks (#2158 slice 1). Passed straight through. */
  pickedCandidateIds?: string[]
  /** Settings for THIS run only (#2175 slice 2), from a run-settings
   * dialog opened by Run rather than from a list. Passed straight
   * through; the saved template is not touched. */
  settingsOverride?: Record<string, unknown>
  /** Stamped onto the local run row. The server owns the authoritative
   * record; these are what the page needs to scope its own view before the
   * first progress message arrives. */
  applicationId?: string
  variantId?: string
}

export interface LiveRun {
  run: TestRun | null
  phase: TestRunPhase | null
  /** The test the run is working on right now, or null. Null whenever the
   * run is not running, or is in a step other than the one that named the
   * test, so a test the server forgot to clear is never shown. */
  currentTest: CurrentTest | null
  /** A start is in flight. Callers disable their controls on this OR on the
   * run being 'running' — the first covers the window before the POST
   * resolves, the second everything after. */
  starting: boolean
  /** 0–100. Pinned to 100 once the run reaches a terminal state, because the
   * backend stops sending updates and a bar frozen at 87% reads as stuck. */
  progressPct: number
  /** Start a run from a saved template. Returns the server's run id, or null
   * if it never started. The server generates the id and pre-creates the row;
   * the socket subscribes to THAT id, never a client-invented one. */
  start: (templateId: string, opts?: StartRunOptions) => Promise<string | null>
  stop: () => Promise<void>
  reset: () => void
  /** Reattach to a run already in flight. Safe to call on mount. */
  resume: (applicationId: string, variantId: string, wantedRunId?: string) => Promise<void>
}

/** The step each kind of current test belongs to. */
const ACTIVITY_PHASE: Record<CurrentTest['activity'], TestRunPhase> = {
  writing: 'generating',
  running: 'executing',
  fixing: 'healing',
  checking: 'verifying',
}

/** What each kind of current test is called on the card. */
const ACTIVITY_WORDS: Record<CurrentTest['activity'], string> = {
  writing: 'writing',
  running: 'running',
  fixing: 'fixing',
  checking: 'checking the fix to',
}

/** The live run card's words for the test in progress, for example
 * `writing "Sign in" (1 of 2)`. */
export function describeCurrentTest(test: CurrentTest): string {
  return `${ACTIVITY_WORDS[test.activity]} "${test.name}" (${test.position} of ${test.total})`
}

/** Wait before the first reconnect attempt, doubled after each failure. */
const RECONNECT_FIRST_MS = 1000
/** The longest wait between reconnect attempts. */
const RECONNECT_MAX_MS = 30_000

/** A run as the page saves it: without `startedAt`, `completedAt` or
 * `duration`. The server records all three from its own clock. The page's
 * values are guesses (a reattach does not know when the run began), and
 * saving them overwrote the real ones. */
function withoutTiming(run: TestRun): Omit<TestRun, 'startedAt' | 'completedAt' | 'duration'> {
  const { startedAt: _s, completedAt: _c, duration: _d, ...rest } = run
  return rest
}

export function useLiveRun(): LiveRun {
  const [run, setRun] = useState<TestRun | null>(null)
  const [phase, setPhase] = useState<TestRunPhase | null>(null)
  const [currentTest, setCurrentTest] = useState<CurrentTest | null>(null)
  const [backendProgressPct, setBackendProgressPct] = useState(0)
  const wsRef = useRef<WebSocket | null>(null)
  /** True from the moment `start` is called until the POST settles.
   *
   * A ref, not state, and deliberately: `setState` does not apply until after
   * the handler returns, so two clicks in the same tick would both see the old
   * value and both fire. Callers disable their buttons on `run.status`, which
   * only becomes 'running' after the POST resolves — leaving a window on a slow
   * network where a second click starts a second run. Runs cost money, so the
   * guard belongs here rather than in each caller's button (Copilot review on
   * PR #2168). */
  const startingRef = useRef(false)
  const [starting, setStarting] = useState(false)

  /** Which attach is current. `start`, `resume`, `reset`, `stop` and unmount
   * each replace it, and work that waited on the server checks it before
   * acting, so a reply that lands after the page moved on does nothing.
   * Cancelling the request instead would not cover a reply already on its
   * way. */
  const attachRef = useRef(0)
  /** The `resume` in flight, so a second call for the same run joins it
   * rather than opening a second socket. In the dev build React runs the
   * page's mount code twice, which is how the page used to attach twice. */
  const pendingResumeRef = useRef<{ key: string; token: number; promise: Promise<void> } | null>(null)
  /** The run whose live updates the hook wants, while it is still going.
   * Cleared when the run ends, is stopped, or is replaced. A socket that
   * drops while this names its run is reconnected; any other close is
   * expected and ignored. */
  const liveRunIdRef = useRef<string | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttemptRef = useRef(0)

  /** Drop the socket and any pending reconnect, and make every attach in
   * flight stale. The socket is detached before it is closed, so its own
   * close event finds it is no longer current and does nothing. */
  const detach = useCallback(() => {
    attachRef.current += 1
    pendingResumeRef.current = null
    liveRunIdRef.current = null
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
    reconnectTimerRef.current = null
    reconnectAttemptRef.current = 0
    const ws = wsRef.current
    wsRef.current = null
    ws?.close()
  }, [])

  // A socket outlives the component unless it is told not to. Without this, a
  // page that mounts and unmounts a few times leaks one connection each time.
  useEffect(() => () => detach(), [detach])

  const handleProgress = useCallback((runId: string, progress: TestRunProgress) => {
    // The run has ended: nothing more to listen for. The server has already
    // saved its final state, so the page saves nothing.
    if (toStatus(progress) !== 'running' && liveRunIdRef.current === runId) liveRunIdRef.current = null
    setPhase(progress.phase ?? null)
    setCurrentTest(progress.currentTest ?? null)
    setBackendProgressPct(progress.progress ?? 0)
    setRun((prev) => {
      // Ignore a message for a run this hook is not watching — the socket can
      // outlive a switch between runs.
      if (!prev || prev.id !== runId) return prev
      return {
        ...prev,
        status: toStatus(progress),
        results: toResults(progress),
        logs: toLogs(progress),
        summary: progress.summary,
        // #1580 — carry the phases + planned/generated counts so the live
        // summary can swap to Planned/Generated for a non-execution run.
        ranPhases: progress.ranPhases,
        plannedCount: progress.plannedCount,
        generatedCount: progress.generatedCount,
        errorReason: progress.errorReason,
      }
    })
  }, [])

  // `subscribe` and `reconnect` call each other, so `reconnect` is reached
  // through a ref rather than a dependency.
  const reconnectRef = useRef<(runId: string, token: number) => void>(() => {})

  const subscribe = useCallback((runId: string, token: number) => {
    const old = wsRef.current
    wsRef.current = null
    old?.close()
    liveRunIdRef.current = runId
    // Each handler acts only while its own socket is the current one. A
    // socket the hook replaced or closed reports nothing.
    const onDrop = () => {
      if (wsRef.current !== ws) return
      wsRef.current = null
      ws.close()
      if (liveRunIdRef.current === runId) reconnectRef.current(runId, token)
    }
    const ws: WebSocket = createWebSocketConnection(
      runId,
      (p: TestRunProgress) => {
        if (wsRef.current !== ws) return
        reconnectAttemptRef.current = 0
        handleProgress(runId, p)
      },
      onDrop,
      onDrop,
    )
    wsRef.current = ws
  }, [handleProgress])

  /** Listen to a run again after its socket dropped. Asks the server where
   * the run is first, so updates missed while disconnected are not lost,
   * and so a run that ended meanwhile shows how it ended. Keeps trying,
   * waiting longer each time, until it succeeds or the page moves on. The
   * run's saved status is never touched. */
  const reconnect = useCallback((runId: string, token: number) => {
    const delay = Math.min(RECONNECT_FIRST_MS * 2 ** reconnectAttemptRef.current, RECONNECT_MAX_MS)
    reconnectAttemptRef.current += 1
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null
      void (async () => {
        if (attachRef.current !== token) return
        let progress: TestRunProgress | null
        try {
          progress = await getTestRunStatus(runId)
        } catch {
          if (attachRef.current === token) reconnect(runId, token)
          return
        }
        if (attachRef.current !== token) return
        // The server no longer knows the run. Nothing left to listen to.
        if (!progress) { liveRunIdRef.current = null; return }
        handleProgress(runId, progress)
        if (toStatus(progress) === 'running') subscribe(runId, token)
      })()
    }, delay)
  }, [handleProgress, subscribe])
  reconnectRef.current = reconnect

  /** Attach to a run already in flight.
   *
   * `wantedRunId` is for the caller who KNOWS which run it wants — it was
   * just started, and the id came back from the trigger. Without it this
   * picks whichever run the active list happens to return first, which
   * with two runs going is a coin flip (#2206). A named run is also
   * attached to whatever its status, because the caller has the id: a run
   * that finished between the trigger and this page mounting should still
   * be shown, not silently dropped.
   *
   * A second call for the same run, while the first is still waiting or
   * already listening, does nothing. */
  const resume = useCallback((applicationId: string, variantId: string, wantedRunId?: string) => {
    const key = `${applicationId}|${variantId}|${wantedRunId ?? ''}`
    const pending = pendingResumeRef.current
    if (pending && pending.key === key && pending.token === attachRef.current) return pending.promise
    if (wantedRunId && wsRef.current && liveRunIdRef.current === wantedRunId) return Promise.resolve()

    detach()
    const token = attachRef.current
    const promise = (async () => {
      let runId = wantedRunId
      if (!runId) {
        const activeRunIds = await getActiveRuns()
        if (attachRef.current !== token || activeRunIds.length === 0) return
        runId = activeRunIds[0]
      }
      const progress = await getTestRunStatus(runId)
      if (attachRef.current !== token || !progress) return
      if (!wantedRunId && progress.status !== 'running') return

      setRun({
        id: runId,
        applicationId,
        variantId,
        testSuiteId: '',
        templateId: '',
        status: toStatus(progress),
        results: toResults(progress),
        logs: toLogs(progress),
        summary: progress.summary,
        ranPhases: progress.ranPhases,
        plannedCount: progress.plannedCount,
        generatedCount: progress.generatedCount,
        errorReason: progress.errorReason,
        // Not the run's real start, which the page does not know. Never
        // saved: see `withoutTiming`.
        startedAt: new Date(),
      })
      setPhase(progress.phase ?? null)
      setCurrentTest(progress.currentTest ?? null)
      // A finished run is shown, not listened to.
      if (toStatus(progress) === 'running') subscribe(runId, token)
    })()
    pendingResumeRef.current = { key, token, promise }
    return promise
  }, [detach, subscribe])

  const start = useCallback(async (templateId: string, opts: StartRunOptions = {}) => {
    if (startingRef.current) return null
    startingRef.current = true
    setStarting(true)
    // The new run replaces whatever was attached, and owns the token from
    // here. If the page resets, unmounts or attaches elsewhere before the
    // server answers, the run still starts but this page does not listen.
    detach()
    const token = attachRef.current
    // #1114 — the server owns the run id. It loads the template, pre-creates
    // the row, starts the run, and returns the id; the socket subscribes to
    // THAT one. A client-invented id used to race the server's, so the socket
    // listened on a channel nobody broadcast to and the row the server updated
    // was not the row the UI was watching.
    let started: TestRun | null = null
    try {
      const { runId } = await triggerRun(templateId, {
        ...(opts.pickedCandidateIds?.length ? { pickedCandidateIds: opts.pickedCandidateIds } : {}),
        ...(opts.settingsOverride ? { settingsOverride: opts.settingsOverride } : {}),
      })
      if (attachRef.current !== token) return runId
      started = {
        id: runId,
        applicationId: opts.applicationId ?? '',
        variantId: opts.variantId ?? '',
        testSuiteId: '',
        templateId,
        status: 'running',
        results: [],
        logs: [
          { timestamp: new Date(), level: 'info', message: `Starting ${opts.label ?? 'run'}…` },
          ...(opts.targetUrl ? [{ timestamp: new Date(), level: 'info' as const, message: `Target: ${opts.targetUrl}` }] : []),
        ],
        startedAt: new Date(),
      }
      setRun(started)
      setPhase(null)
      setCurrentTest(null)
      setBackendProgressPct(0)
      subscribe(runId, token)
      return runId
    } catch (error) {
      if (attachRef.current !== token) return null
      const message = error instanceof Error ? error.message : 'Failed to start test run'
      // Two failure shapes, and they need different handling. If `started` is
      // set the server DID create a row, so it has to be marked failed or it
      // sits "running" forever. If not, there is no server state and the
      // failure is local only.
      if (started) {
        const failed: TestRun = {
          ...started,
          status: 'failed',
          logs: [...started.logs, { timestamp: new Date(), level: 'error', message }],
          completedAt: new Date(),
        }
        void testRunsAPI.update(failed.id, withoutTiming(failed))
        setRun(failed)
      } else {
        setRun({
          id: `local-${Date.now()}`,
          applicationId: opts.applicationId ?? '',
          variantId: opts.variantId ?? '',
          testSuiteId: '',
          templateId,
          status: 'failed',
          results: [],
          logs: [
            { timestamp: new Date(), level: 'info', message: `Starting ${opts.label ?? 'run'}…` },
            { timestamp: new Date(), level: 'error', message },
          ],
          startedAt: new Date(),
          completedAt: new Date(),
        })
      }
      return null
    } finally {
      startingRef.current = false
      setStarting(false)
    }
  }, [detach, subscribe])

  const stop = useCallback(async () => {
    detach()
    setRun((prev) => {
      if (!prev) return prev
      void (async () => {
        try { await apiCancelTestRun(prev.id) } catch { /* best effort */ }
      })()
      const stopped: TestRun = {
        ...prev,
        status: 'cancelled',
        completedAt: new Date(),
        logs: [...prev.logs, { timestamp: new Date(), level: 'warn', message: 'Test run cancelled by user' }],
      }
      void testRunsAPI.update(stopped.id, withoutTiming(stopped))
      return stopped
    })
  }, [detach])

  const reset = useCallback(() => {
    detach()
    setRun(null)
    setPhase(null)
    setCurrentTest(null)
    setBackendProgressPct(0)
  }, [detach])

  const terminal = run?.status === 'completed' || run?.status === 'failed' || run?.status === 'cancelled'
  return {
    run,
    phase,
    currentTest: !terminal && run && currentTest && ACTIVITY_PHASE[currentTest.activity] === phase ? currentTest : null,
    starting,
    progressPct: terminal ? 100 : backendProgressPct,
    start,
    stop,
    reset,
    resume,
  }
}
